import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Teleprompter for a single segment — editable before recording, auto-scrolls while recording.
 */
export function TeleprompterPanel({
  text,
  active = false,
  editable = false,
  onTextChange,
  speed = 42,
  className = '',
}) {
  const scrollRef = useRef(null);
  const [draft, setDraft] = useState(text || '');
  const showEditor = editable && !active;

  useEffect(() => {
    setDraft(text || '');
  }, [text]);

  useLayoutEffect(() => {
    if (!active || showEditor || !scrollRef.current) return undefined;
    const el = scrollRef.current;
    // Rendered inside a Picture-in-Picture window, the opener tab may be hidden and its rAF paused.
    const win = el.ownerDocument?.defaultView || window;
    let raf;
    const tick = () => {
      el.scrollTop += speed / 60;
      raf = win.requestAnimationFrame(tick);
    };
    raf = win.requestAnimationFrame(tick);
    return () => win.cancelAnimationFrame(raf);
  }, [active, showEditor, speed, draft]);

  return (
    <div
      className={`relative rounded-xl border border-slate-700 bg-black/90 overflow-hidden flex flex-col ${className}`}
      data-testid="teleprompter-panel"
    >
      {editable ? (
        <div className="shrink-0 px-3 py-1.5 border-b border-slate-700/80 text-[10px] uppercase tracking-wide text-slate-500">
          {active ? 'Recording — script locked' : 'Edit script below · auto-scrolls when you record'}
        </div>
      ) : null}
      <div className="relative flex-1 min-h-0">
        <div className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-black to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black to-transparent z-10 pointer-events-none" />
        {showEditor ? (
          <textarea
            data-testid="teleprompter-editor"
            value={draft}
            onChange={(e) => {
              const next = e.target.value;
              setDraft(next);
              onTextChange?.(next);
            }}
            className="w-full h-full min-h-[12rem] resize-y bg-transparent px-6 py-10 text-center text-lg sm:text-xl leading-relaxed text-white font-medium whitespace-pre-wrap border-0 outline-none focus:ring-0 placeholder:text-slate-600"
            placeholder="Type or paste what you want to read on camera…"
            spellCheck
          />
        ) : (
          <div
            ref={scrollRef}
            data-testid="teleprompter-scroll"
            className="h-full overflow-y-auto px-6 py-16 text-center text-lg sm:text-xl leading-relaxed text-white whitespace-pre-wrap font-medium"
          >
            {draft || '—'}
          </div>
        )}
      </div>
    </div>
  );
}

export default TeleprompterPanel;
